/**
 * Removes identifying metadata from an image before it leaves the device.
 *
 * Phone cameras embed GPS coordinates, capture timestamps, device model
 * and sometimes a serial number in EXIF. Uploaded photos here are
 * world-readable by URL, and this is a diving app — the coordinates of a
 * site somebody photographed are exactly the kind of thing they may not
 * have meant to publish.
 *
 * This edits the container, it does NOT re-encode. Re-encoding through a
 * canvas is the usual trick, but it loses quality, flattens animated
 * GIFs, and depends on the browser applying the EXIF orientation flag —
 * where any inconsistency silently rotates every portrait photo. Walking
 * the segment structure avoids all three: the compressed image data is
 * copied through byte for byte.
 *
 * Orientation is the one EXIF tag deliberately preserved. It is not
 * identifying, and dropping it would turn portrait photos sideways.
 */

const JPEG_APP1 = 0xe1;
const EXIF_PREFIX = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"
const XMP_PREFIX = "http://ns.adobe.com/xap/";
const ORIENTATION_TAG = 0x0112;

// JPEG markers carrying metadata we drop wholesale. APP0 (JFIF) and APP2
// (ICC colour profile) are deliberately kept — dropping ICC shifts colour.
const DROPPED_JPEG_MARKERS = new Set([
  0xe1, // APP1  EXIF / XMP
  0xed, // APP13 Photoshop / IPTC
  0xee, // APP14 Adobe
  0xfe, // COM   free-text comment
]);

function startsWith(bytes, offset, prefix) {
  for (let i = 0; i < prefix.length; i++) {
    if (bytes[offset + i] !== prefix[i]) return false;
  }
  return true;
}

/** Reads the EXIF orientation out of an APP1 payload, or null. */
function readOrientation(bytes, tiffStart) {
  const view = new DataView(bytes.buffer, bytes.byteOffset);
  const byteOrder = view.getUint16(tiffStart);
  let little;
  if (byteOrder === 0x4949) little = true;
  else if (byteOrder === 0x4d4d) little = false;
  else return null;

  if (view.getUint16(tiffStart + 2, little) !== 42) return null;
  const ifdOffset = view.getUint32(tiffStart + 4, little);
  const ifd = tiffStart + ifdOffset;
  if (ifd + 2 > bytes.length) return null;

  const count = view.getUint16(ifd, little);
  for (let i = 0; i < count; i++) {
    const entry = ifd + 2 + i * 12;
    if (entry + 12 > bytes.length) return null;
    if (view.getUint16(entry, little) === ORIENTATION_TAG) {
      const value = view.getUint16(entry + 8, little);
      return value >= 1 && value <= 8 ? value : null;
    }
  }
  return null;
}

/** A minimal big-endian EXIF APP1 segment carrying only Orientation. */
function buildOrientationSegment(orientation) {
  const segment = new Uint8Array(36);
  const view = new DataView(segment.buffer);
  let o = 0;
  view.setUint16(o, 0xffe1); o += 2;       // APP1
  view.setUint16(o, 34); o += 2;           // length (excludes the marker)
  segment.set(EXIF_PREFIX, o); o += 6;     // "Exif\0\0"
  view.setUint16(o, 0x4d4d); o += 2;       // big endian
  view.setUint16(o, 42); o += 2;
  view.setUint32(o, 8); o += 4;            // IFD0 starts 8 bytes in
  view.setUint16(o, 1); o += 2;            // one entry
  view.setUint16(o, ORIENTATION_TAG); o += 2;
  view.setUint16(o, 3); o += 2;            // SHORT
  view.setUint32(o, 1); o += 4;            // count
  view.setUint16(o, orientation); o += 2;  // value, left-aligned in 4 bytes
  view.setUint16(o, 0); o += 2;            // padding
  view.setUint32(o, 0);                    // no next IFD
  return segment;
}

function stripJpeg(bytes) {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) return null; // not a JPEG
  const keep = [bytes.subarray(0, 2)];
  const view = new DataView(bytes.buffer, bytes.byteOffset);
  let orientation = null;
  let offset = 2;

  while (offset < bytes.length) {
    if (bytes[offset] !== 0xff) return null; // desynced; leave the file alone
    const marker = bytes[offset + 1];

    // Standalone markers carry no length field.
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
      keep.push(bytes.subarray(offset, offset + 2));
      offset += 2;
      continue;
    }
    // Start of scan: entropy-coded data runs to the end. Copy verbatim.
    if (marker === 0xda) {
      keep.push(bytes.subarray(offset));
      break;
    }
    if (offset + 4 > bytes.length) return null;
    const length = view.getUint16(offset + 2);
    const end = offset + 2 + length;
    if (length < 2 || end > bytes.length) return null;

    let drop = DROPPED_JPEG_MARKERS.has(marker);
    // A file can carry several APP1 segments — EXIF plus XMP, or EXIF
    // split across segments — and only the first EXIF IFD0 is
    // authoritative. Without the null guard a later segment that has no
    // orientation tag would erase the one we already found.
    if (
      orientation === null &&
      marker === JPEG_APP1 &&
      startsWith(bytes, offset + 4, EXIF_PREFIX)
    ) {
      orientation = readOrientation(bytes, offset + 10);
    }
    if (!drop) keep.push(bytes.subarray(offset, end));
    offset = end;
  }

  if (orientation !== null && orientation !== 1) {
    keep.splice(1, 0, buildOrientationSegment(orientation));
  }
  return concat(keep);
}

// PNG metadata lives in ancillary chunks; the image data (IDAT) is
// untouched, and every chunk carries its own CRC so nothing needs
// recomputing.
const DROPPED_PNG_CHUNKS = new Set(["eXIf", "tEXt", "zTXt", "iTXt", "tIME"]);
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

function stripPng(bytes) {
  if (!startsWith(bytes, 0, PNG_SIGNATURE)) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset);
  const keep = [bytes.subarray(0, 8)];
  let offset = 8;

  while (offset + 8 <= bytes.length) {
    const length = view.getUint32(offset);
    const type = String.fromCharCode(...bytes.subarray(offset + 4, offset + 8));
    const end = offset + 12 + length; // length + type + data + crc
    if (end > bytes.length) return null;
    if (!DROPPED_PNG_CHUNKS.has(type)) keep.push(bytes.subarray(offset, end));
    offset = end;
    if (type === "IEND") break;
  }
  return concat(keep);
}

// WebP is a RIFF container; EXIF and XMP are their own chunks, and the
// RIFF size in the header has to be corrected after removing any.
function stripWebp(bytes) {
  const tag = (o) => String.fromCharCode(...bytes.subarray(o, o + 4));
  if (tag(0) !== "RIFF" || tag(8) !== "WEBP") return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset);
  const keep = [bytes.subarray(0, 12)];
  let offset = 12;
  let removed = 0;

  while (offset + 8 <= bytes.length) {
    const type = tag(offset);
    const size = view.getUint32(offset + 4, true);
    const padded = size + (size % 2); // chunks are even-aligned
    const end = offset + 8 + padded;
    if (end > bytes.length) return null;
    if (type === "EXIF" || type === "XMP ") removed += 8 + padded;
    else keep.push(bytes.subarray(offset, end));
    offset = end;
  }
  if (removed === 0) return concat(keep);

  const out = concat(keep);
  new DataView(out.buffer).setUint32(4, out.length - 8, true);
  return out;
}

function concat(parts) {
  let total = 0;
  for (const p of parts) total += p.length;
  const out = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    out.set(p, o);
    o += p.length;
  }
  return out;
}

/** Strips a byte array by sniffing the container. Returns null when the
 * format isn't handled or the file looks malformed — callers upload the
 * original rather than risk mangling it. */
export function stripMetadataBytes(bytes, type) {
  try {
    if (type === "image/jpeg") return stripJpeg(bytes);
    if (type === "image/png") return stripPng(bytes);
    if (type === "image/webp") return stripWebp(bytes);
    // GIF has no EXIF container, so there is nothing to remove.
    return null;
  } catch (err) {
    return null;
  }
}

/**
 * Returns a File with metadata removed, or the original if the format
 * isn't handled or anything looks off. Never throws: failing to strip
 * must not block an upload, and mangling a photo is worse than leaving
 * its metadata in place.
 */
export async function stripMetadata(file) {
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const stripped = stripMetadataBytes(bytes, file.type);
    if (!stripped || stripped.length === 0 || stripped.length > bytes.length) {
      return file;
    }
    return new File([stripped], file.name, { type: file.type, lastModified: Date.now() });
  } catch (err) {
    return file;
  }
}
